import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtQty, API_BASE } from "../lib/api";
import { haptic } from "../lib/mobile";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { ArrowLeft, Star, StarOff, Sparkles, Loader2, CandlestickChart as CandleIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import CandlestickChart from "../components/CandlestickChart";
import ChartCopilot from "../components/ChartCopilot";
import PreTradeNudge from "../components/PreTradeNudge";
import IndicatorSubPanels from "../components/IndicatorSubPanels";

const RANGES = ["1D", "1W", "1M", "1Y"];

const Row = ({ k, v }) => (
  <div className="flex items-center justify-between">
    <span className="text-muted-foreground">{k}</span>
    <span className="font-mono-tab font-medium text-foreground">{v}</span>
  </div>
);

export default function AssetDetail() {
  const { assetType, symbol } = useParams();
  const navigate = useNavigate();
  const { refreshUser, user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [range, setRange] = useState("1M");
  const [history, setHistory] = useState([]);
  const [side, setSide] = useState("buy");
  const [qty, setQty] = useState("");
  const [sizeMode, setSizeMode] = useState("shares"); // shares | dollars — fractional support
  const [dollarInput, setDollarInput] = useState("");
  const [placing, setPlacing] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const [pendingOrderQty, setPendingOrderQty] = useState(0);
  const [inWatchlist, setInWatchlist] = useState(false);
  const [insight, setInsight] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [ibkr, setIbkr] = useState(null);
  const [ibkrMcp, setIbkrMcp] = useState(null);
  const [route, setRoute] = useState("sim");
  const [exitMode, setExitMode] = useState("off");     // "off" | "fixed" | "trailing"
  const [exitStopPct, setExitStopPct] = useState("5");
  const [exitTpPct, setExitTpPct] = useState("10");
  const [exitTrailPct, setExitTrailPct] = useState("5");
  const [flash, setFlash] = useState(null);
  const [product, setProduct] = useState("spot"); // spot | perp — crypto only
  const [perpSide, setPerpSide] = useState("long");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [showCandles, setShowCandles] = useState(false);
  const [aRange, setARange] = useState("1M");
  const [timeframe, setTimeframe] = useState("auto");
  const [activeIndicators, setActiveIndicators] = useState(["sma20", "sma50"]);
  // Risk sizing
  const [stopPrice, setStopPrice] = useState("");
  const [riskProfile, setRiskProfile] = useState(null); // {profile, equity, realized_today}
  const [riskPreview, setRiskPreview] = useState(null); // {suggested_qty, budget_usd, ...}

  useEffect(() => {
    api.get("/settings/risk").then((r) => setRiskProfile(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      const [a, wl, ib, mcp] = await Promise.allSettled([
        api.get(`/assets/${assetType}/${symbol}`),
        api.get("/watchlist"),
        api.get("/ibkr/status"),
        api.get("/ibkr/mcp/status"),
      ]);
      if (a.status === "fulfilled") setAsset(a.value.data);
      if (wl.status === "fulfilled") setInWatchlist(wl.value.data.some((x) => x.symbol === symbol));
      if (ib.status === "fulfilled") setIbkr(ib.value.data);
      if (mcp.status === "fulfilled") setIbkrMcp(mcp.value.data);
    })();
  }, [assetType, symbol]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/assets/${assetType}/${symbol}/history?range=${range}`);
      setHistory(data.points);
    })().catch(() => {});
  }, [assetType, symbol, range]);

  const loadAnalysis = async (r = aRange, tf = timeframe) => {
    try {
      const { data } = await api.get(`/analysis/${assetType}/${symbol}?range=${r}&timeframe=${tf}`);
      setAnalysis(data);
    } catch { toast.error("Chart analysis unavailable"); }
  };
  useEffect(() => {
    if (showCandles && !analysis) loadAnalysis();
  }, [showCandles]);
  useEffect(() => {
    if (showCandles) loadAnalysis(aRange, timeframe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe]);

  const toggleWatch = async () => {
    if (inWatchlist) {
      await api.delete(`/watchlist/${symbol}`);
      setInWatchlist(false);
      toast.success("Removed from watchlist");
    } else {
      await api.post(`/watchlist`, { symbol, asset_type: assetType });
      setInWatchlist(true);
      toast.success("Added to watchlist");
    }
  };

  const placeOrder = async () => {
    if (sizeMode === "dollars") {
      const d = parseFloat(dollarInput);
      if (!d || d < 1) return toast.error("Minimum order is $1");
      if (product === "perp") return toast.error("Perps require share quantity, not dollars");
      setPendingOrderQty(asset?.price ? d / asset.price : 0);
    } else {
      const quantity = parseFloat(qty);
      if (!quantity || quantity <= 0) return toast.error("Enter a valid quantity");
      setPendingOrderQty(quantity);
    }
    setNudgeOpen(true);
  };

  const doPlaceOrder = async () => {
    const quantity = pendingOrderQty;
    setPlacing(true);
    try {
      const useDollars = sizeMode === "dollars" && product !== "perp";
      const dollars = useDollars ? parseFloat(dollarInput) : null;
      const body = {
        symbol, asset_type: product === "perp" ? "perp" : assetType,
        side, route,
        ...(useDollars ? { dollar_amount: dollars } : { quantity }),
        ...(product === "perp" ? { perp: { position_side: perpSide, leverage: 1 } } : {}),
      };
      const { data } = await api.post("/orders", body);
      const label = route === "sim" ? "Sim" : route === "ibkr_paper" ? "IBKR paper" : route === "ibkr_live" ? "IBKR live" : "IBKR 1-tap";
      const kind = product === "perp" ? `Perp ${perpSide}` : "Spot";
      haptic(side);
      setFlash(side);
      setTimeout(() => setFlash(null), 800);
      // Undo toast — reverses the trade if tapped within 8 seconds
      const filledQty = data?.order?.quantity ?? quantity;
      const undoBody = {
        symbol, asset_type: body.asset_type,
        side: side === "buy" ? "sell" : "buy",
        quantity: filledQty, route,
        ...(product === "perp" ? { perp: { position_side: perpSide, leverage: 1 } } : {}),
      };
      toast.success(
        `${side === "buy" ? "Bought" : "Sold"} ${fmtQty(filledQty)} ${asset.ticker} · ${kind} @ ${fmtMoney(data.order.price)} · ${label}`,
        { duration: 8000, action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/orders", undoBody);
              haptic(undoBody.side);
              toast.info("Order reversed");
              await refreshUser();
            } catch (e) { toast.error("Undo failed"); }
          },
        }}
      );
      setQty("");
      setDollarInput("");
      await refreshUser();

      // Auto-attach exit rules on a successful order (Buy = long, Sell = short cover).
      if (exitMode !== "off" && product !== "perp" && assetType !== "option") {
        try {
          const body = {
            symbol, asset_type: assetType,
            quantity: filledQty,
            direction: side === "buy" ? "long" : "short",
            route: route === "ibkr_mcp" ? "sim" : route,
          };
          const entry = data.order.price;
          if (exitMode === "trailing") {
            const t = parseFloat(exitTrailPct);
            if (!t || t <= 0) throw new Error("Invalid trail %");
            body.trailing_pct = t;
            if (parseFloat(exitTpPct) > 0) {
              // For longs TP is above entry; for shorts TP is below entry.
              body.take_price = side === "buy"
                ? entry * (1 + parseFloat(exitTpPct) / 100)
                : entry * (1 - parseFloat(exitTpPct) / 100);
            }
          } else {
            const sl = parseFloat(exitStopPct);
            const tp = parseFloat(exitTpPct);
            if (side === "buy") {
              if (sl > 0) body.stop_price = entry * (1 - sl / 100);
              if (tp > 0) body.take_price = entry * (1 + tp / 100);
            } else {
              // Short: stop is ABOVE entry (risk if price climbs), TP is BELOW entry
              if (sl > 0) body.stop_price = entry * (1 + sl / 100);
              if (tp > 0) body.take_price = entry * (1 - tp / 100);
            }
          }
          await api.post("/stops", body);
          toast.success(
            exitMode === "trailing"
              ? `Trailing ${side === "buy" ? "stop" : "cover"} attached (${exitTrailPct}%)`
              : `SL${exitStopPct}%/TP${exitTpPct}% attached`
          );
        } catch (e) {
          toast.warning("Order filled but exit rule couldn't be attached: " + (e?.response?.data?.detail || e.message));
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Order failed");
    } finally {
      setPlacing(false);
      setReviewOpen(false);
    }
  };

  const askAI = async () => {
    if (!asset) return;
    setAiLoading(true);
    setInsight("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/ai/insights`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ symbol, asset_type: assetType, price: asset.price, change_pct: asset.change_pct }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const chunks = buf.split("\n\n");
        buf = chunks.pop() || "";
        for (const c of chunks) {
          if (c.startsWith("data: ")) {
            const t = c.slice(6);
            if (t === "[DONE]") continue;
            if (t.startsWith("[ERROR")) { toast.error(t); continue; }
            setInsight((prev) => prev + t);
          }
        }
      }
    } catch (e) {
      toast.error("AI request failed");
    } finally {
      setAiLoading(false);
    }
  };

  if (!asset) return <div className="p-8 text-muted-foreground">Loading...</div>;

  const gain = asset.change_pct >= 0;
  const chartData = history.map((p) => ({ t: p.t, price: p.price }));
  const effectiveQty = sizeMode === "dollars" && product !== "perp" && asset?.price
    ? (parseFloat(dollarInput) || 0) / asset.price
    : parseFloat(qty) || 0;
  const estimatedCost = sizeMode === "dollars" && product !== "perp"
    ? (parseFloat(dollarInput) || 0)
    : effectiveQty * (asset?.price || 0);
  const canBuy = estimatedCost <= (user?.cash_balance || 0);

  return (
    <div className="space-y-6" data-testid="asset-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          {asset.image ? <img src={asset.image} alt="" className="w-14 h-14 rounded-full" /> :
            <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center font-mono-tab font-semibold">{asset.ticker.slice(0, 3)}</div>}
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-2xl md:text-3xl font-bold">{asset.name}</h1>
              <span className="text-xs uppercase text-muted-foreground border border-border px-2 py-0.5 rounded-full">{asset.ticker}</span>
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="flex items-center gap-2 mt-1 flex-wrap">
              <div className="font-mono-tab text-2xl md:text-3xl font-semibold" data-testid="asset-price">{fmtMoney(asset.price, asset.price > 100 ? 2 : 4)}</div>
              <div className={`font-mono-tab ${gain ? "gain" : "loss"}`}>{fmtPct(asset.change_pct)}</div>
              {asset.source === "ibkr_mcp" && (
                <span title={`IBKR live · bid ${asset.bid} / ask ${asset.ask}`}
                      data-testid="price-source-ibkr"
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[10px] font-mono-tab uppercase tracking-wider">
                  <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" /> IBKR Live
                </span>
              )}
              {asset.source === "alpaca" && (
                <span data-testid="price-source-alpaca"
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-secondary/60 text-muted-foreground text-[10px] font-mono-tab uppercase tracking-wider">
                  Alpaca
                </span>
              )}
              {asset.source === "coingecko" && (
                <span data-testid="price-source-coingecko"
                      className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-secondary/60 text-muted-foreground text-[10px] font-mono-tab uppercase tracking-wider">
                  CoinGecko
                </span>
              )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {assetType === "stock" && (
            <Button variant="outline" onClick={() => navigate(`/options/${symbol}`)} className="rounded-full" data-testid="asset-options-btn">
              Options chain
            </Button>
          )}
          <Button variant={inWatchlist ? "outline" : "default"} onClick={toggleWatch}
                  className={`rounded-full font-semibold ${inWatchlist ? "" : "bg-amber-400 hover:bg-amber-300 text-black"}`}
                  data-testid="watchlist-toggle">
            {inWatchlist
              ? <><StarOff className="w-4 h-4 mr-2" /> On watchlist · Remove</>
              : <><Star className="w-4 h-4 mr-2 fill-current" /> Add to watchlist</>}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="lg:col-span-2 p-6 border-border rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-semibold">Price history</div>
            <div className="flex gap-1" data-testid="range-tabs">
              {RANGES.map((r) => (
                <button key={r}
                        onClick={() => setRange(r)}
                        data-testid={`range-${r}`}
                        className={`px-3 py-1 text-xs rounded-full transition-colors ${range === r ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="chart-gr" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={gain ? "hsl(var(--gain))" : "hsl(var(--loss))"} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={gain ? "hsl(var(--gain))" : "hsl(var(--loss))"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="t" hide />
                <YAxis dataKey="price" domain={["auto", "auto"]} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                       tickFormatter={(v) => `$${v.toFixed(0)}`} width={60} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                         labelFormatter={(t) => new Date(t).toLocaleString()}
                         formatter={(v) => [fmtMoney(v), "Price"]} />
                <Area type="monotone" dataKey="price" stroke={gain ? "hsl(var(--gain))" : "hsl(var(--loss))"}
                      strokeWidth={2} fill="url(#chart-gr)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-border">
            <div><div className="text-xs text-muted-foreground uppercase">Market cap</div>
                 <div className="font-mono-tab font-medium mt-1">{fmtMoney(asset.market_cap, 0)}</div></div>
            <div><div className="text-xs text-muted-foreground uppercase">24h Volume</div>
                 <div className="font-mono-tab font-medium mt-1">{fmtMoney(asset.volume, 0)}</div></div>
            <div><div className="text-xs text-muted-foreground uppercase">Type</div>
                 <div className="font-medium mt-1 capitalize">{asset.asset_type}</div></div>
          </div>
          <div className="mt-4 pt-3 border-t border-border">
            <button onClick={() => setShowCandles((v) => !v)} data-testid="toggle-candles"
                    className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground flex items-center gap-1">
              <CandleIcon className="w-3.5 h-3.5" /> {showCandles ? "Hide" : "Show"} candlestick analysis
            </button>
          </div>
        </Card>

        {/* Order + AI */}
        <div className="space-y-6">
          <Card className={`p-6 border-border rounded-xl ${flash === "buy" ? "flash-buy" : flash === "sell" ? "flash-sell" : ""}`}
                data-testid="order-ticket">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-semibold">Trade</div>
              {assetType === "crypto" && (
                <div className="inline-flex bg-secondary/50 rounded-full p-0.5" data-testid="product-toggle">
                  <button onClick={() => setProduct("spot")}
                          className={`px-3 py-0.5 text-[11px] font-medium rounded-full ${product === "spot" ? "bg-card shadow-sm" : "text-muted-foreground"}`}
                          data-testid="product-spot">Spot</button>
                  <button onClick={() => setProduct("perp")}
                          className={`px-3 py-0.5 text-[11px] font-medium rounded-full ${product === "perp" ? "bg-card shadow-sm" : "text-muted-foreground"}`}
                          data-testid="product-perp">Perp</button>
                </div>
              )}
            </div>
            {product === "perp" && (
              <div className="mb-3 grid grid-cols-2 gap-1 bg-secondary/50 rounded-full p-1" data-testid="perp-side">
                <button onClick={() => setPerpSide("long")} data-testid="perp-long"
                        className={`py-1 text-xs font-medium rounded-full ${perpSide === "long" ? "bg-[hsl(var(--gain))] text-black" : "text-muted-foreground"}`}>Long</button>
                <button onClick={() => setPerpSide("short")} data-testid="perp-short"
                        className={`py-1 text-xs font-medium rounded-full ${perpSide === "short" ? "bg-[hsl(var(--loss))] text-black" : "text-muted-foreground"}`}>Short</button>
              </div>
            )}
            <div className="flex bg-secondary/50 rounded-full p-1 mb-4">
              <button onClick={() => setSide("buy")} data-testid="side-buy"
                      className={`flex-1 py-1.5 text-sm font-medium rounded-full transition ${side === "buy" ? "bg-[hsl(var(--gain))] text-black" : "text-muted-foreground"}`}>Buy</button>
              <button onClick={() => setSide("sell")} data-testid="side-sell"
                      className={`flex-1 py-1.5 text-sm font-medium rounded-full transition ${side === "sell" ? "bg-[hsl(var(--loss))] text-black" : "text-muted-foreground"}`}>Sell</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase text-muted-foreground">Route</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-1 p-1 bg-secondary/50 rounded-lg" data-testid="route-selector">
                  {[
                    { v: "sim", label: "Sim", enabled: true },
                    { v: "ibkr_paper", label: "IBKR Paper", enabled: ibkr?.connected && ibkr?.mode === "paper" },
                    { v: "ibkr_live", label: "IBKR Live", enabled: ibkr?.connected && ibkr?.mode === "live" },
                    { v: "ibkr_mcp", label: "IBKR · 1-tap", enabled: !!ibkrMcp?.connected },
                  ].map((r) => (
                    <button
                      key={r.v}
                      disabled={!r.enabled}
                      onClick={() => setRoute(r.v)}
                      data-testid={`route-${r.v}`}
                      className={`py-1.5 text-[11px] font-medium rounded-md transition ${
                        route === r.v ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
                {route === "ibkr_mcp" && (
                  <div className="mt-1.5 text-[11px] text-emerald-400/90 leading-relaxed p-2 rounded-md bg-emerald-500/[0.06] border border-emerald-500/25">
                    Drafts into IBKR's <span className="font-semibold text-emerald-300">AI Instructions</span> tab.
                    You confirm inside IBKR before it fills. Safest live mode.
                  </div>
                )}
                {!ibkr?.connected && !ibkrMcp?.connected && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    <button onClick={() => navigate("/settings")} className="underline">Connect IBKR</button> to route real orders.
                  </div>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs uppercase text-muted-foreground">
                    {sizeMode === "dollars" ? "Amount ($)" : "Quantity"}
                  </label>
                  {product !== "perp" && assetType !== "option" && (
                    <div className="inline-flex bg-secondary/50 rounded-full p-0.5" data-testid="size-mode-toggle">
                      <button onClick={() => setSizeMode("shares")}
                              data-testid="size-mode-shares"
                              className={`px-2.5 py-0.5 text-[10px] font-medium rounded-full uppercase tracking-wider transition ${sizeMode === "shares" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                        Shares
                      </button>
                      <button onClick={() => setSizeMode("dollars")}
                              data-testid="size-mode-dollars"
                              className={`px-2.5 py-0.5 text-[10px] font-medium rounded-full uppercase tracking-wider transition ${sizeMode === "dollars" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                        $ Amount
                      </button>
                    </div>
                  )}
                </div>
                {sizeMode === "dollars" && product !== "perp" ? (
                  <>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono-tab pointer-events-none">$</span>
                      <Input type="number" min="1" step="1" value={dollarInput}
                             onChange={(e) => setDollarInput(e.target.value)}
                             className="font-mono-tab pl-6" placeholder="50"
                             data-testid="order-dollar-amount" />
                    </div>
                    {side === "buy" && (
                      <div className="mt-2 grid grid-cols-4 gap-1" data-testid="dollar-quick">
                        {[10, 50, 100, 500].map((v) => (
                          <button key={v} onClick={() => setDollarInput(String(v))}
                                  data-testid={`dollar-quick-${v}`}
                                  className="text-[11px] py-1 rounded-md bg-secondary/40 hover:bg-secondary/70 text-muted-foreground hover:text-foreground transition font-mono-tab">
                            ${v}
                          </button>
                        ))}
                      </div>
                    )}
                    {dollarInput && asset?.price && (
                      <div className="mt-1.5 text-[11px] text-muted-foreground" data-testid="dollar-preview">
                        ≈ <span className="font-mono-tab text-foreground">{fmtQty(effectiveQty)}</span> {asset.ticker} @ {fmtMoney(asset.price)}
                      </div>
                    )}
                  </>
                ) : (
                  <Input type="number" min="0" step="any" value={qty} onChange={(e) => setQty(e.target.value)}
                         className="font-mono-tab" placeholder="0.00" data-testid="order-qty" />
                )}
              </div>

              {/* Risk sizer — enter a stop and we back-solve qty from your per-trade risk budget */}
              {side === "buy" && riskProfile && (
                <div className="rounded-lg border border-border p-3 bg-secondary/30" data-testid="risk-sizer">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase text-muted-foreground flex items-center gap-1">
                      <span>Risk sizer</span>
                    </div>
                    <button
                      onClick={() => navigate("/settings")}
                      data-testid="risk-defaults-link"
                      className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
                      {riskProfile.profile.unit === "pct"
                        ? `${riskProfile.profile.value}% per trade`
                        : `$${riskProfile.profile.value} per trade`}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Input type="number" min="0" step="any" value={stopPrice}
                             onChange={(e) => setStopPrice(e.target.value)}
                             placeholder="Stop price"
                             className="font-mono-tab" data-testid="risk-stop-input" />
                    </div>
                    <Button variant="outline" size="sm"
                            data-testid="risk-suggest-btn"
                            onClick={async () => {
                              const sp = parseFloat(stopPrice);
                              if (!sp || sp <= 0 || sp === asset.price)
                                return toast.error("Enter a stop below your entry (for longs)");
                              try {
                                const { data } = await api.post("/risk/size", {
                                  symbol, asset_type: product === "perp" ? "perp" : assetType,
                                  entry_price: asset.price, stop_price: sp, side: "buy",
                                });
                                setRiskPreview(data);
                                setQty(String(data.suggested_qty));
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || "Sizing failed");
                              }
                            }}
                            className="rounded-full">
                      Size trade
                    </Button>
                  </div>
                  {riskPreview && (
                    <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]" data-testid="risk-preview">
                      <div>
                        <div className="text-muted-foreground">Risk</div>
                        <div className="font-mono-tab">{fmtMoney(riskPreview.budget_usd)}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">% equity</div>
                        <div className="font-mono-tab">{riskPreview.risk_pct_of_equity}%</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Qty</div>
                        <div className="font-mono-tab">{fmtQty(riskPreview.suggested_qty)}</div>
                      </div>
                    </div>
                  )}
                  <div className="text-[10px] text-muted-foreground mt-2">
                    Uses your default — {riskProfile.profile.unit === "pct" ? `${riskProfile.profile.value}% of equity` : `$${riskProfile.profile.value} per trade`}. Change in Settings.
                  </div>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Est. total</span>
                <span className="font-mono-tab font-medium" data-testid="order-total">{fmtMoney(estimatedCost)}</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Available cash</span>
                <span className="font-mono-tab">{fmtMoney(user?.cash_balance)}</span>
              </div>

              {/* Liquidity check — compare current 24h $ volume to your gate */}
              {riskProfile?.profile?.min_volume_usd_24h != null && asset?.volume != null && (() => {
                const dollarVol = assetType === "crypto" ? asset.volume : asset.volume * asset.price;
                const floor = riskProfile.profile.min_volume_usd_24h;
                const ok = dollarVol >= floor;
                return (
                  <div className={`text-[11px] flex items-center justify-between rounded-md px-2 py-1 border ${ok ? "border-border text-muted-foreground" : "border-[hsl(var(--loss))]/40 loss bg-[hsl(var(--loss))]/10"}`}
                       data-testid="liquidity-chip">
                    <span>24h $ volume</span>
                    <span className="font-mono-tab">
                      {fmtMoney(dollarVol, 0)} {ok ? "≥" : "<"} {fmtMoney(floor, 0)}
                    </span>
                  </div>
                );
              })()}

              {/* Attach exit rules — available on both Buy (protects new longs) AND Sell (short covers) */}
              {product !== "perp" && assetType !== "option" && (
                <div className="border-t border-border/40 pt-3" data-testid="exit-rules">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase text-muted-foreground">
                      {side === "buy" ? "Attach exit rules · long" : "Attach exit rules · short cover"}
                    </div>
                    <div className="inline-flex bg-secondary/50 rounded-full p-0.5" data-testid="exit-mode-toggle">
                      {["off", "fixed", "trailing"].map((m) => (
                        <button key={m}
                                onClick={() => setExitMode(m)}
                                data-testid={`exit-mode-${m}`}
                                className={`px-2 py-0.5 text-[10px] font-medium rounded-full uppercase tracking-wider transition ${
                                  exitMode === m
                                    ? (m === "trailing" ? "bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))]" : "bg-card text-foreground shadow-sm")
                                    : "text-muted-foreground"
                                }`}>
                          {m === "off" ? "Off" : m === "fixed" ? "Fixed" : "Trail"}
                        </button>
                      ))}
                    </div>
                  </div>
                  {exitMode === "fixed" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] text-muted-foreground">SL %</div>
                        <Input type="number" min="0" step="0.5" value={exitStopPct}
                               onChange={(e) => setExitStopPct(e.target.value)}
                               data-testid="exit-stop-pct" className="mt-0.5 h-8 text-xs font-mono-tab" />
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">TP %</div>
                        <Input type="number" min="0" step="0.5" value={exitTpPct}
                               onChange={(e) => setExitTpPct(e.target.value)}
                               data-testid="exit-tp-pct" className="mt-0.5 h-8 text-xs font-mono-tab" />
                      </div>
                      {asset?.price && (parseFloat(exitStopPct) > 0 || parseFloat(exitTpPct) > 0) && (
                        <div className="col-span-2 text-[10px] font-mono-tab text-muted-foreground">
                          {parseFloat(exitStopPct) > 0 && (
                            <>SL <span className="loss">{fmtMoney(asset.price * (1 - parseFloat(exitStopPct) / 100))}</span></>
                          )}
                          {parseFloat(exitStopPct) > 0 && parseFloat(exitTpPct) > 0 && " · "}
                          {parseFloat(exitTpPct) > 0 && (
                            <>TP <span className="gain">{fmtMoney(asset.price * (1 + parseFloat(exitTpPct) / 100))}</span></>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {exitMode === "trailing" && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] text-muted-foreground">Trail %</div>
                        <Input type="number" min="0.1" max="50" step="0.5" value={exitTrailPct}
                               onChange={(e) => setExitTrailPct(e.target.value)}
                               data-testid="exit-trail-pct" className="mt-0.5 h-8 text-xs font-mono-tab" />
                      </div>
                      <div>
                        <div className="text-[10px] text-muted-foreground">TP % (optional)</div>
                        <Input type="number" min="0" step="0.5" value={exitTpPct}
                               onChange={(e) => setExitTpPct(e.target.value)}
                               data-testid="exit-trail-tp-pct" className="mt-0.5 h-8 text-xs font-mono-tab" />
                      </div>
                      {asset?.price && parseFloat(exitTrailPct) > 0 && (
                        <div className="col-span-2 text-[10px] font-mono-tab text-muted-foreground">
                          Initial stop: <span className="loss">{fmtMoney(asset.price * (1 - parseFloat(exitTrailPct) / 100))}</span>
                          <span className="ml-1 opacity-70">· climbs with price</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Button onClick={() => {
                        if (sizeMode === "dollars" && product !== "perp") {
                          const d = parseFloat(dollarInput);
                          if (!d || d < 1) return toast.error("Minimum order is $1");
                        } else {
                          const q = parseFloat(qty);
                          if (!q || q <= 0) return toast.error("Enter a valid quantity");
                        }
                        setReviewOpen(true);
                      }}
                      disabled={placing || (!qty && !dollarInput) || (side === "buy" && !canBuy)}
                      data-testid="order-submit"
                      className={`w-full rounded-full h-11 ${side === "buy" ? "bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black" : "bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-black"}`}>
                {placing ? "Placing..." : `Review ${side === "buy" ? "buy" : "sell"} ${asset.ticker}`}
              </Button>
              {side === "buy" && !canBuy && (qty || dollarInput) && <div className="text-xs loss text-center">Insufficient cash</div>}
            </div>
          </Card>

          {/* Order review dialog — nothing hits the market until Confirm */}
          <AlertDialog open={reviewOpen} onOpenChange={setReviewOpen}>
            <AlertDialogContent data-testid="order-review-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  Review {side} order
                </AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="pt-2 space-y-2 text-sm">
                    <Row k="Asset" v={`${asset.ticker} · ${product === "perp" ? `Perp ${perpSide}` : product === "spot" ? assetType : assetType}`} />
                    <Row k="Side" v={side.toUpperCase()} />
                    <Row k="Quantity" v={fmtQty(effectiveQty)} />
                    <Row k="Price" v={fmtMoney(asset.price)} />
                    <Row k="Est. total" v={fmtMoney(estimatedCost)} />
                    <Row k="Route" v={route === "sim" ? "Simulated" : route === "ibkr_paper" ? "IBKR Paper" : route === "ibkr_live" ? "IBKR Live" : "IBKR · 1-tap confirm"} />
                    {sizeMode === "dollars" && product !== "perp" && (
                      <div className="text-[11px] text-muted-foreground italic">
                        Fractional order — filled at market against your ${parseFloat(dollarInput || 0).toFixed(2)}.
                      </div>
                    )}
                    {riskPreview && side === "buy" && (
                      <div className="mt-1 p-2 rounded-md bg-secondary/50 border border-border">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Risk</div>
                        <Row k="Max loss if stop hits" v={`${fmtMoney(riskPreview.budget_usd)} · ${riskPreview.risk_pct_of_equity}% of equity`} />
                        <Row k="Stop price" v={fmtMoney(parseFloat(stopPrice))} />
                      </div>
                    )}
                    <div className="pt-2 text-xs text-muted-foreground">Market orders fill immediately at the current price. You have 8 seconds to undo.</div>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="review-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="review-confirm"
                  className={side === "buy"
                    ? "bg-[hsl(var(--gain))] hover:bg-[hsl(var(--gain))]/90 text-black"
                    : "bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-black"}
                  onClick={placeOrder}
                >
                  Confirm {side}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Card className="p-6 rounded-xl ai-tint" data-testid="ai-insight-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" />
                <div className="font-display font-semibold">AI Coach</div>
              </div>
              <Button size="sm" variant="ghost" onClick={askAI} disabled={aiLoading} data-testid="ai-ask-btn"
                      className="rounded-full text-[hsl(var(--ai))] hover:bg-[hsl(var(--ai))]/10">
                {aiLoading ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Thinking</> : "Ask"}
              </Button>
            </div>
            <div className="text-sm whitespace-pre-wrap min-h-[80px]" data-testid="ai-insight-text">
              {insight || <span className="text-muted-foreground">Get an analysis of {asset.ticker} rooted in classic trading books.</span>}
            </div>
          </Card>
        </div>
      </div>

      {showCandles && (
        <ChartAnalysisPanel
          analysis={analysis}
          aRange={aRange}
          setARange={(r) => { setARange(r); loadAnalysis(r); }}
          timeframe={timeframe}
          setTimeframe={setTimeframe}
          activeIndicators={activeIndicators}
          setActiveIndicators={setActiveIndicators}
          symbol={symbol}
          assetType={assetType}
        />
      )}

      {/* Chart Copilot — beginner-friendly setup detector */}
      <ChartCopilot
        symbol={symbol}
        assetType={assetType}
        currentPrice={asset.price}
        onPrefillOrder={({ side, price }) => {
          setSide(side);
          // Prefill quantity with a sensible default based on cash on hand
          if (user?.cash_balance && price) {
            const q = Math.max(0.01, Math.floor((user.cash_balance * 0.05 / price) * 100) / 100);
            setQty(String(q));
          }
          toast.success(`Order ticket prefilled to ${side} at $${price.toFixed(2)}`);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      />

      {/* Behavioral nudge check before every order confirm */}
      <PreTradeNudge
        open={nudgeOpen}
        onOpenChange={setNudgeOpen}
        onProceed={doPlaceOrder}
        order={pendingOrderQty > 0 ? {
          asset_type: product === "perp" ? "perp" : assetType,
          symbol, side, qty: pendingOrderQty, price: asset?.price || 0,
        } : null}
      />
    </div>
  );
}

const TIMEFRAMES = [
  { v: "auto", label: "Auto" },
  { v: "1m", label: "1m" },
  { v: "5m", label: "5m" },
  { v: "15m", label: "15m" },
  { v: "1h", label: "1H" },
  { v: "1d", label: "1D" },
  { v: "1w", label: "1W" },
];

const INDICATORS = [
  { v: "sma20", label: "SMA 20", type: "overlay" },
  { v: "sma50", label: "SMA 50", type: "overlay" },
  { v: "ema20", label: "EMA 20", type: "overlay" },
  { v: "ema50", label: "EMA 50", type: "overlay" },
  { v: "bb", label: "Bollinger", type: "overlay" },
  { v: "vwap", label: "VWAP", type: "overlay" },
  { v: "fib", label: "Fibonacci", type: "overlay" },
  { v: "rsi14", label: "RSI 14", type: "panel" },
  { v: "macd", label: "MACD", type: "panel" },
  { v: "stoch", label: "Stochastic", type: "panel" },
  { v: "adx14", label: "ADX", type: "panel" },
  { v: "atr14", label: "ATR", type: "panel" },
  { v: "obv", label: "OBV", type: "panel" },
];

function ChartAnalysisPanel({ analysis, aRange, setARange, timeframe, setTimeframe, activeIndicators, setActiveIndicators, symbol, assetType }) {
  const [enabled, setEnabled] = useState(() => new Set());
  const [allOrders, setAllOrders] = useState([]);
  const [markerFilter, setMarkerFilter] = useState("all"); // all | sim | ibkr_paper | ibkr_live | last10
  const catalog = analysis?.catalog || [];
  useEffect(() => {
    if (catalog.length && enabled.size === 0) setEnabled(new Set(catalog));
  }, [catalog.length, catalog, enabled.size]);

  useEffect(() => {
    api.get("/orders").then(({ data }) => {
      setAllOrders(data.filter((o) => o.symbol === symbol && o.asset_type === assetType));
    }).catch(() => {});
  }, [symbol, assetType]);

  // Marker filter applied
  const filteredOrders = (() => {
    const sorted = [...allOrders].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    if (markerFilter === "all") return sorted;
    if (markerFilter === "last10") return sorted.slice(-10);
    return sorted.filter((o) => o.route === markerFilter);
  })();

  const orderMarkers = filteredOrders.map((o) => ({
    id: o.id, t: new Date(o.created_at).getTime(),
    price: o.price, side: o.side,
    ticker: o.ticker, quantity: o.quantity,
    total: o.total, route: o.route,
  }));

  // FIFO matching for round-trip P&L segments + weighted avg cost of remaining
  const { pnlSegments, costBasis } = (() => {
    const sorted = [...allOrders].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const lots = [];   // {qty, price, t}
    const segs = [];
    for (const o of sorted) {
      const t = new Date(o.created_at).getTime();
      if (o.side === "buy") { lots.push({ qty: o.quantity, price: o.price, t }); }
      else {
        let remaining = o.quantity;
        while (remaining > 1e-9 && lots.length) {
          const lot = lots[0];
          const q = Math.min(remaining, lot.qty);
          const pnl = (o.price - lot.price) * q;
          segs.push({ tStart: lot.t, tEnd: t, pnl });
          lot.qty -= q; remaining -= q;
          if (lot.qty <= 1e-9) lots.shift();
        }
      }
    }
    const totQty = lots.reduce((s, l) => s + l.qty, 0);
    const totCost = lots.reduce((s, l) => s + l.qty * l.price, 0);
    return { pnlSegments: segs, costBasis: totQty > 0 ? totCost / totQty : null };
  })();

  if (!analysis) {
    return (
      <div className="rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
        Loading candles & patterns…
      </div>
    );
  }
  const filtered = (analysis.patterns || []).filter((p) => enabled.has(p.name));
  const bull = filtered.filter((p) => p.kind === "bull").length;
  const bear = filtered.filter((p) => p.kind === "bear").length;

  const toggle = (name) => {
    setEnabled((cur) => {
      const next = new Set(cur);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  return (
    <div className="rounded-xl border border-border p-4 md:p-6 space-y-4" data-testid="chart-analysis">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-semibold flex items-center gap-2">
          <CandleIcon className="w-4 h-4" /> Chart analysis
        </div>
        <div className="flex gap-1" data-testid="analysis-range">
          {["1D", "1W", "1M", "3M", "1Y"].map((r) => (
            <button key={r} onClick={() => setARange(r)}
                    data-testid={`ar-${r}`}
                    className={`px-2.5 py-1 text-xs rounded-full ${aRange === r ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>
              {r}
            </button>
          ))}
        </div>
      </div>

      {/* Timeframe pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="analysis-timeframe">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Timeframe</span>
        {TIMEFRAMES.map((tf) => (
          <button
            key={tf.v}
            onClick={() => setTimeframe(tf.v)}
            data-testid={`tf-${tf.v}`}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
              timeframe === tf.v
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:border-foreground/40"
            }`}
          >
            {tf.label}
          </button>
        ))}
      </div>

      {/* Indicator toggles */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="analysis-indicators">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Indicators</span>
        {INDICATORS.map((ind) => {
          const on = activeIndicators.includes(ind.v);
          return (
            <button
              key={ind.v}
              onClick={() => setActiveIndicators(
                on ? activeIndicators.filter((v) => v !== ind.v)
                   : [...activeIndicators, ind.v]
              )}
              data-testid={`ind-${ind.v}`}
              className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                on
                  ? (ind.type === "overlay"
                      ? "bg-[hsl(var(--ai))]/15 border-[hsl(var(--ai))] text-[hsl(var(--ai))]"
                      : "bg-amber-500/15 border-amber-500 text-amber-500")
                  : "border-border text-muted-foreground hover:border-foreground/40"
              }`}
            >
              {ind.label}
            </button>
          );
        })}
      </div>

      {/* Marker filter */}
      {allOrders.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="marker-filter">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Show</span>
          {[
            { v: "all", l: `All (${allOrders.length})` },
            { v: "sim", l: "Sim" },
            { v: "ibkr_paper", l: "IBKR Paper" },
            { v: "ibkr_live", l: "IBKR Live" },
            { v: "last10", l: "Last 10" },
          ].map((o) => (
            <button key={o.v} onClick={() => setMarkerFilter(o.v)}
                    data-testid={`marker-filter-${o.v}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${markerFilter === o.v ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:border-foreground/40"}`}>
              {o.l}
            </button>
          ))}
        </div>
      )}

      {/* Pattern filter chips — user picks which patterns matter */}
      <div className="flex flex-wrap gap-1.5" data-testid="pattern-filter">
        {catalog.map((name) => {
          const on = enabled.has(name);
          const kind = (analysis.patterns.find((p) => p.name === name) || {}).kind;
          const cls = kind === "bull" ? "bg-[hsl(var(--gain))]/20 border-[hsl(var(--gain))] gain"
                    : kind === "bear" ? "bg-[hsl(var(--loss))]/20 border-[hsl(var(--loss))] loss"
                    : "bg-[hsl(var(--ai))]/20 border-[hsl(var(--ai))]";
          return (
            <button key={name} onClick={() => toggle(name)}
                    data-testid={`filter-${name.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? cls : "border-border text-muted-foreground hover:border-foreground/40"}`}>
              {name}
            </button>
          );
        })}
      </div>

      <CandlestickChart candles={analysis.candles} patterns={filtered} indicators={analysis.indicators} activeIndicators={activeIndicators} markers={orderMarkers} costBasis={costBasis} pnlSegments={pnlSegments} symbol={`${assetType}:${symbol}`} />
      <IndicatorSubPanels candles={analysis.candles} indicators={analysis.indicators} activeIndicators={activeIndicators} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm pt-2 border-t border-border">
        <div><div className="text-xs text-muted-foreground uppercase">Bullish signals</div><div className="font-mono-tab gain">{bull}</div></div>
        <div><div className="text-xs text-muted-foreground uppercase">Bearish signals</div><div className="font-mono-tab loss">{bear}</div></div>
        <div><div className="text-xs text-muted-foreground uppercase">RSI 14</div><div className="font-mono-tab">{analysis.indicators.rsi14 ?? "—"}</div></div>
        <div><div className="text-xs text-muted-foreground uppercase">Trend</div><div className="capitalize">{analysis.indicators.trend}</div></div>
      </div>

      <div className="space-y-1 max-h-52 overflow-y-auto pt-2">
        {filtered.slice().reverse().slice(0, 10).map((p, i) => (
          <div key={i} className="flex items-start gap-2 text-xs" data-testid={`pattern-log-${i}`}>
            <span className={`shrink-0 px-1.5 py-0.5 rounded uppercase text-[10px] ${
              p.kind === "bull" ? "bg-[hsl(var(--gain))]/20 gain" :
              p.kind === "bear" ? "bg-[hsl(var(--loss))]/20 loss" :
              "bg-[hsl(var(--ai))]/20 text-[hsl(var(--ai))]"
            }`}>{p.name}</span>
            <span className="text-muted-foreground">{p.description}</span>
          </div>
        ))}
        {!filtered.length && <div className="text-xs text-muted-foreground italic text-center py-4">No patterns for the selected filters.</div>}
      </div>
    </div>
  );
}
